import { z } from 'zod';
/**
 * Runtime configuration an administrator owns, and the vocabulary it drives.
 *
 * Its own module rather than part of `master.ts` for the reason `legal.ts` gives:
 * three surfaces read the grading vocabulary — the API's exporter, the console's
 * badges, and the settings form itself — and only one of them is master data.
 *
 * ONE VALUE LIVES HERE TODAY. `app_settings` is a typed single-row table, and
 * DAT-04's FR-16 fraud thresholds are expected to join it as further columns
 * with their own request schema. They are deliberately absent until that work
 * lands, because a threshold nothing reads is a threshold nobody maintains.
 */
/* -------------------------------------------------------------------------- */
/* The competition index grade — FR-14                                        */
/* -------------------------------------------------------------------------- */
/**
 * How a competition plan's observed index reads against its target.
 *
 * THE INDEX IS OURS ÷ THEIRS, so LOWER IS BETTER: we are cheaper than the rival
 * we benchmarked against. That direction is the whole reason this enum is not
 * symmetric — `VERY_GOOD` sits at the BOTTOM of the range and `NEED_ADJUSTMENT`
 * at the top, which is the opposite of every attainment metric in the reporting
 * module and is the mistake a reader will make if the comment is not here.
 *
 * The bands, given a plan's `targetIndex` and the configured threshold `t`:
 *
 *     index <= target × (1 − t)          VERY_GOOD
 *     target × (1 − t) < index < × (1+t) GOOD
 *     index >= target × (1 + t)          NEED_ADJUSTMENT
 *
 * The bounds are INCLUSIVE ON THE OUTSIDE — exactly `target × (1 − t)` is
 * `VERY_GOOD`, exactly `target × (1 + t)` is `NEED_ADJUSTMENT`. That is the rule
 * as the product owner stated it, and it is the reason the classification runs
 * in PostgreSQL rather than here: `numeric` compares in exact decimal, so a plan
 * sitting precisely on a bound grades the same way on the server, in the export
 * and on screen. See the `competition_grade()` function in migration 0023 and
 * `packages/db/tests/pgtap/competition_grade.sql`, which owns the property.
 *
 * THERE IS NO FOURTH MEMBER FOR "NO DATA". A plan nobody has priced carries a
 * null grade beside its null `actualIndex` — the same statement, made once.
 */
export const CompetitionGrade = z.enum(['VERY_GOOD', 'GOOD', 'NEED_ADJUSTMENT']);
/**
 * The grade as the product owner says it, in Indonesian.
 *
 * IN THE CONTRACTS AND NOT RESTATED PER SURFACE, which is a deliberate departure
 * from how `price-export.service.ts` restates `rupiah()` and `multiplier()`.
 * Those are FORMATTERS — two implementations of "render a number" can differ by
 * a thousands separator and nobody is misled. These are TERMS OF ART the product
 * owner defined; an export whose wording drifts from the screen it was exported
 * from is a support conversation about whether the two disagree.
 */
export const COMPETITION_GRADE_LABELS = Object.freeze({
    VERY_GOOD: 'Sangat baik',
    GOOD: 'Baik',
    NEED_ADJUSTMENT: 'Perlu penyesuaian',
});
/* -------------------------------------------------------------------------- */
/* app_settings                                                                */
/* -------------------------------------------------------------------------- */
/**
 * The half-width of the `GOOD` band, as a FRACTION of each plan's target.
 *
 * 0.04 — the product owner's 4% — means a 1.0000 target grades `VERY_GOOD` at or
 * below 0.9600 and `NEED_ADJUSTMENT` at or above 1.0400, and a 1.5000 target at
 * 1.4400 and 1.5600. Relative and not absolute for the reason the old
 * `COMPETITION_INDEX_TOLERANCE` gave: an absolute ±0.04 is 4.4% of a 0.9 target
 * and 1.7% of a 2.4 one, three times stricter on one benchmark than the other
 * for no reason anyone can state.
 *
 * A FRACTION ON THE WIRE, A PERCENTAGE ON SCREEN. The form converts in two
 * expressions; the column, this contract and every SQL expression see only the
 * fraction. A threshold that is a percentage in some layers and a fraction in
 * others is off by a hundred exactly once, in production.
 *
 * Bounded below 1 rather than at some judgement about a sane band: at t = 1 the
 * lower bound is 0 and no plan can ever be `VERY_GOOD`, which makes the grade
 * meaningless rather than merely lax. The database carries the same bound as a
 * CHECK.
 *
 * Four decimals, checked here rather than left to the column, for the reason
 * `TargetIndex` gives — `numeric(5,4)` rounds silently, and a threshold that is
 * not the number the administrator typed is worse than a rejected form.
 */
export const CompetitionIndexThreshold = z
    .number()
    .positive()
    .lt(1)
    .refine((value) => Number(value.toFixed(4)) === value, {
    message: 'ambang maksimal empat angka di belakang koma',
});
/**
 * `PUT /masterdata/settings/price-index`.
 *
 * Not `.partial()` like the other update requests in this package: there is one
 * field, so a PATCH with nothing in it would be a request that validates and
 * does nothing. PUT with the whole (one-field) resource is the honest verb.
 */
export const UpdatePriceIndexSettingsRequest = z.object({
    competitionIndexThreshold: CompetitionIndexThreshold,
});
/* -------------------------------------------------------------------------- */
/* The planogram grading bands — FR-05                                        */
/* -------------------------------------------------------------------------- */
/**
 * The administrator-owned half of FR-05 grading — all four metric bands.
 *
 * EIGHT VALUES IN TWO SHAPES, and the shapes are the whole design. Every metric
 * is now ABSOLUTE: nothing is compared against a per-SKU target, so every band is
 * a pair of integers on one axis.
 *
 *   MORE IS BETTER — availability and facing. An A-MINIMUM and a C-MAXIMUM, with
 *   B as the gap between them. This is the shape availability already had.
 *
 *   LOWER IS BETTER — sequence and row. An A-MAXIMUM and a B-MAXIMUM, with C as
 *   everything past the second. Position 1 is the best position on both axes, so
 *   there is no lower bound to set.
 *
 * The refinements below are what stop either shape collapsing a grade, and each
 * is a rule no per-field validator can see. They are not symmetric, deliberately:
 * "more is better" needs C STRICTLY below A because the two bands are tested
 * independently and would otherwise overlap at equality; "lower is better" needs
 * only A <= B because the bands are tested in order and touching is harmless.
 *
 * WHAT IS NOT HERE AT ALL: the weights, the grade points and the rank bands. The
 * product owner fixed those, so they are constants in `planogram.js` rather than
 * columns — see the note there about printing them versus scoring with them.
 *
 * RETROACTIVITY DIFFERS FROM THE PRICE BAND ABOVE, and the settings screen must
 * say so. `competition_grade()` classifies on read, so moving that threshold
 * re-grades history. These bands are applied by the scorer and PERSISTED per row,
 * so changing them affects visits scored afterwards and nothing already stored.
 */
export const PlanogramGradingSettings = z
    .object({
    /**
     * Availability — distinct internal SKUs of one division detected across ALL
     * photos of a visit. VISIT GRAIN, not photo grain: a product found on any
     * shelf in the outlet is stocked, and counting it once per photograph would
     * grade the same outlet differently for taking more pictures.
     */
    availabilityAMin: z.number().int().min(1).max(1000),
    availabilityCMax: z.number().int().min(0).max(999),
    /**
     * Facing — units of one product side by side, per SKU per photo. Absolute
     * since the shelf plan was dropped: four facings is four facings whatever
     * anyone once planned for the product.
     */
    facingAMin: z.number().int().min(1).max(1000),
    facingCMax: z.number().int().min(0).max(999),
    /**
     * Sequence — position from the left of the shelf row, counting every product
     * including a rival's. LOWER IS BETTER, so these are ceilings.
     */
    sequenceAMax: z.number().int().min(1).max(999),
    sequenceBMax: z.number().int().min(1).max(1000),
    /** Row — shelf level counting from the top. Lower is better, as above. */
    rowAMax: z.number().int().min(1).max(999),
    rowBMax: z.number().int().min(1).max(1000),
})
    /*
     * STRICTLY LESS THAN on the "more is better" pairs. At C = A the bands OVERLAP
     * rather than merely touch: a count equal to both satisfies "Z >= A therefore
     * A" and "Z <= C therefore C", so the grade would depend on which branch the
     * scorer happens to test first. Above it, B becomes unreachable — which
     * destroys a grade rather than tightening one, the same argument this file
     * already makes for refusing a zero-width price band.
     */
    .refine((value) => value.availabilityCMax < value.availabilityAMin, {
    message: 'Batas C ketersediaan harus lebih kecil dari batas A',
    path: ['availabilityCMax'],
})
    .refine((value) => value.facingCMax < value.facingAMin, {
    message: 'Batas C facing harus lebih kecil dari batas A',
    path: ['facingCMax'],
})
    /*
     * AT MOST, not strictly less, on the "lower is better" pairs — and the
     * difference is not an oversight. These bands are tested in order (A first,
     * then B, else C), so A = B is a legal configuration meaning "grade B is
     * unreachable because A already covers everything B would". That is a
     * deliberate setting an administrator may want; A > B is the incoherent one,
     * because it would make the B test unreachable while pretending otherwise.
     */
    .refine((value) => value.sequenceAMax <= value.sequenceBMax, {
    message: 'Batas A urutan tidak boleh lebih besar dari batas B',
    path: ['sequenceAMax'],
})
    .refine((value) => value.rowAMax <= value.rowBMax, {
    message: 'Batas A baris tidak boleh lebih besar dari batas B',
    path: ['rowAMax'],
});
/**
 * `PUT /planogram/settings`.
 *
 * The whole object, like `UpdateFraudSettingsRequest` and for the same reason:
 * the cross-field rule above cannot be checked against a partial.
 */
export const UpdatePlanogramSettingsRequest = PlanogramGradingSettings;
/* -------------------------------------------------------------------------- */
/* The vision pipeline — ADR-0013                                             */
/* -------------------------------------------------------------------------- */
/**
 * How a photograph's reading is presented — the one remaining vision toggle.
 *
 * IT USED TO CARRY `detectorEnabled` TOO, and scoring v4 removed it. Measured
 * geometry is now the only path, because per-facing sequence grading needs real
 * box positions and the cloud model reports a product ordinal instead. A flag
 * that could turn that off would be a flag that silently changes what a score
 * means, which is the one thing a scoring input must never be.
 *
 * The runtime fallback survives the flag: a detector that fails to load still
 * degrades to model geometry rather than losing the reading (ADR-0013 decision
 * 5), and `PhotoAnalysis.geometrySource` still records which path produced each
 * one. What is gone is the ability to CHOOSE the worse path.
 */
export const VisionSettings = z.object({
    /**
     * Whether a photograph's reading shows ONLY detections matched to Master SKU
     * — migration 0056.
     *
     * ON THIS ENDPOINT RATHER THAN A NEW ONE, per the rule
     * `vision-settings.controller.ts` states: one shared `SettingsService`, one
     * endpoint per domain, under that domain's permission. Both flags decide how a
     * photograph is read, both are ADMIN-only, and both are global; a second
     * controller would buy a second permission for the same decision.
     *
     * APPLIED ON READ, to all history, in both directions — nothing is discarded
     * while it is on, so nothing has to be recovered when it goes off. That is
     * what made it safe to default ON, and it is the opposite of how the retired
     * `detectorEnabled` behaved.
     */
    registeredSkusOnly: z.boolean(),
});
/** `PUT /vision/settings`. The whole object, like the other two settings writes. */
export const UpdateVisionSettingsRequest = VisionSettings;
//# sourceMappingURL=settings.js.map